import { createTheme } from '@mui/material/styles';

/**
 * 佛法搜尋資料庫 - Material-UI 主題配置
 *
 * 配色理念：
 * - 主色調：褐色 (#8b4513) - 象徵樸實、沉穩、禪意
 * - 次要色：金色 (#ffd700) - 象徵智慧、光明
 * - 背景色：米白色 (#f5f5dc) - 柔和、舒適的閱讀體驗
 */

const theme = createTheme({
  palette: {
    primary: {
      main: '#8b4513', // 褐色（主色調）
      light: '#a0522d', // 淺褐色
      dark: '#654321', // 深褐色
      contrastText: '#ffffff', // 白色文字
    },
    secondary: {
      main: '#ffd700', // 金色（次要色）
      light: '#ffed4e', // 淺金色
      dark: '#c7a600', // 深金色
      contrastText: '#000000', // 黑色文字
    },
    background: {
      default: '#f5f5dc', // 米白色（頁面背景）
      paper: '#ffffff', // 白色（卡片背景）
    },
    text: {
      primary: '#333333', // 深灰色（主要文字）
      secondary: '#666666', // 灰色（次要文字）
    },
    error: {
      main: '#d32f2f', // 紅色（錯誤訊息）
    },
    warning: {
      main: '#ff9800', // 橙色（警告訊息）
    },
    info: {
      main: '#0288d1', // 藍色（資訊訊息）
    },
    success: {
      main: '#388e3c', // 綠色（成功訊息）
    },
  },
  typography: {
    fontFamily: [
      // 繁體中文優先字型
      'Microsoft JhengHei', // 微軟正黑體
      'PingFang TC', // 蘋果繁體中文
      'Noto Sans TC', // Google 思源黑體繁體
      // 簡體中文字型
      'Microsoft YaHei', // 微軟雅黑
      'PingFang SC', // 蘋果簡體中文
      'Noto Sans SC', // Google 思源黑體簡體
      // 英文字型
      'Arial',
      'Helvetica',
      'sans-serif',
    ].join(','),
    fontSize: 14, // 基礎字體大小
    h1: {
      fontSize: '2.5rem', // 40px
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2rem', // 32px
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.75rem', // 28px
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.5rem', // 24px
      fontWeight: 500,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.25rem', // 20px
      fontWeight: 500,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1rem', // 16px
      fontWeight: 500,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem', // 16px
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem', // 14px
      lineHeight: 1.6,
    },
    button: {
      textTransform: 'none', // 按鈕文字不自動大寫
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8, // 統一的圓角大小
  },
  spacing: 8, // 基礎間距單位（8px）
  components: {
    // AppBar（頂部導覽列）樣式
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)', // 柔和的陰影
        },
      },
    },
    // Button（按鈕）樣式
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
        },
        contained: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
          },
        },
      },
    },
    // Card（卡片）樣式
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          },
        },
      },
    },
    // TextField（輸入框）樣式
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
              borderColor: '#8b4513',
            },
          },
        },
      },
    },
    // Drawer（側邊欄）樣式
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#ffffff',
          borderRight: '1px solid rgba(0, 0, 0, 0.12)',
        },
      },
    },
  },
});

export default theme;
